import { Component, signal, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterOutlet, RouterModule } from '@angular/router';
import { ToastModule } from 'primeng/toast';
import { SelectModule } from 'primeng/select';
import { CalService } from './auth/cal.service';
import { UserPreferenceService } from './services/user-preference/user-preference.service';
import { LoaderComponent } from './shared/components/loader/loader.component';
import { CommonModule } from '@angular/common';
import { RegionRoleService } from './services/region-role.service';
import { Router } from '@angular/router';
import { PlanManagerService } from './_services/plan-manager.service';
import { ActivePlanService } from './_services/active-plan.service';
import { PrimengModule } from './shared/modules/primeng/primeng.module';
import { environment } from '../environments/environment';
import { NotificationService } from './_services/notification.service';
import { PublishPlanProgressComponent } from './shared/components/publish-plan-progress/publish-plan-progress.component';
import { HeaderComponent, FooterComponent } from '@cvx/nextpage';
import { GenerateMovementExecutionTrackerService } from './shared/services/generate-movement-execution-tracker.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, FormsModule, PrimengModule, LoaderComponent, HeaderComponent, FooterComponent, CommonModule, PublishPlanProgressComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent implements OnInit {
  welcomeName: string = 'Planning Workspace';
  regionValue: any = '';
  roleValue: any = '';
  displayRegionValue: any = '';
  regionSignal = signal('');
  // Dropdown properties
  dropdownOptions: { id: string; label: string }[] = [];
  selectedActivePlan: string | null = null;

  constructor(
    private calService: CalService,
    private messageService: NotificationService,
    private regionRoleService: RegionRoleService,
    private planManagerService: PlanManagerService,
    private router: Router,
    private activePlanService: ActivePlanService,
    private generateMovementExecutionTrackerService: GenerateMovementExecutionTrackerService
  ) {
    this.setaadtoken();
    // Subscribe to user name changes
    this.calService.getFirstName().subscribe(name => {
      if (name && name.trim()) {
        this.welcomeName = name;
      } else {
        this.welcomeName = 'Planning Workspace'; // Fallback
      }
    });
    // Listen for region/role changes via service (reactive)
    this.regionRoleService.region$.subscribe(region => {
      this.regionValue = region;
      this.loadDropdownOptions();
    });
    this.regionRoleService.role$.subscribe(role => {
      this.roleValue = role;
    });
    this.regionRoleService.displayRegion$.subscribe(displayRegion => {
      this.displayRegionValue = displayRegion;
    });
    this.regionRoleService.listenToStorageEvents();
  }

  ngOnInit(): void {
    this.generateMovementExecutionTrackerService.startTracking();

    this.activePlanService.activePlan$.subscribe(plan => {
      if (plan && plan.refresh) {
        // This is a refresh signal, reload dropdown options
        this.loadDropdownOptions();
      } else if (plan && plan.id) {
        // ActivePlanService persists the selection and notifies same-tab consumers.
        this.loadDropdownOptions(plan.id);
      } else if (plan === null) {
        // Plan was deleted/cleared, reload dropdown options
        this.loadDropdownOptions();
      }
    });
  }

  loadDropdownOptions(selectedId?: string) {
    const region = this.regionValue || localStorage.getItem('selectedRegion') || '';
    if (region) {
      this.planManagerService.getActivePlansForPlanManager(region).subscribe((activePlansResponse: any) => {
        if (activePlansResponse && activePlansResponse.value && typeof activePlansResponse.value === 'object') {
          this.dropdownOptions = Object.entries(activePlansResponse.value.openedPlans).map(([id, label]: [string, any]) => ({
            id,
            label: label as string
          }));
          
          // Always get the latest currentPlanID from localStorage
          const currentPlanID = localStorage.getItem('currentPlanID');
          const newId = selectedId || currentPlanID;
          
          // Check if API response has a currentPlan
          let apiCurrentPlanId = null;
          if (activePlansResponse.value.currentPlan && typeof activePlansResponse.value.currentPlan === 'object') {
            apiCurrentPlanId = Object.keys(activePlansResponse.value.currentPlan)[0];
          }
          
          // Priority: selectedId -> apiCurrentPlanId -> localStorage currentPlanID -> first option
          if (newId && this.dropdownOptions.some(opt => opt.id === newId)) {
            this.selectedActivePlan = newId;
          } else if (apiCurrentPlanId && this.dropdownOptions.some(opt => opt.id === apiCurrentPlanId)) {
            this.selectedActivePlan = apiCurrentPlanId;
            this.activePlanService.setActivePlan({ id: this.selectedActivePlan });
          } else if (this.dropdownOptions.length > 0) {
            // If no valid selection found, select the first available option
            this.selectedActivePlan = this.dropdownOptions[0].id;
            this.activePlanService.setActivePlan({
              id: this.selectedActivePlan,
              label: this.dropdownOptions[0].label
            });
          } else {
            this.selectedActivePlan = null;
            if (this.activePlanService.getActivePlan() !== null) {
              this.activePlanService.clearActivePlan();
            }
          }
        } else {
          this.dropdownOptions = [];
          this.selectedActivePlan = null;
        }
      });
    } else {
      this.dropdownOptions = [];
      this.selectedActivePlan = null;
    }
  }

  onActivePlanChange(planId: string) {
    this.selectedActivePlan = planId;
    // Update shared service so Plan Manager dropdown updates
    this.activePlanService.setActivePlan({ id: planId, label: this.dropdownOptions.find(opt => opt.id === planId)?.label || '' });
    const region = this.regionValue || localStorage.getItem('selectedRegion') || '';
    if (region && planId) {
      this.planManagerService.savePlan(region, planId).subscribe({
        next: (response) => {
          this.messageService.showSuccess('Plan saved successfully');
        },
        error: (error) => {
          this.messageService.showError('Error saving plan from app header');
        }
      });
    }
  }

  private async setaadtoken() {
    const token = await this.calService.getAuthToken();
    if (token) {
      // Store token in session storage
      window.sessionStorage.setItem('AuthToken', token as string);
      // this.authSubject.next(true);
    }
  }

  navigateToModel(event?: Event) {
    if (event) {
      event.stopPropagation();
    }
    // Always navigate to /model, regardless of localStorage or region selection
    this.router.navigate(['/model']);
  }

  navigateToPlanningWorkspaceHome() {
    this.router.navigate(['/planning-workspace-home']);
  }
}
